import { Link } from 'react-router-dom';
import { formatUtcInTimezone } from '../../lib/time.js';
import { EmptyState } from '../ui/empty-state.jsx';
import { TierBadge } from '../nodes/node-table.jsx';

function StatusBadge({ cert }) {
  if (cert.status === 'revoked') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
        <i className="fa-solid fa-ban text-[9px]" aria-hidden="true" />
        Revoked
      </span>
    );
  }
  if (cert.isExpired) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
        <i className="fa-solid fa-hourglass-end text-[9px]" aria-hidden="true" />
        Expired
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
      <i className="fa-solid fa-circle-check text-[9px]" aria-hidden="true" />
      Active
    </span>
  );
}

function NodeCell({ node }) {
  if (!node) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-warning">
        <i className="fa-solid fa-triangle-exclamation text-[10px]" aria-hidden="true" />
        Orphan
      </span>
    );
  }
  return (
    <div className="min-w-0">
      <Link
        to="/nodes"
        className="block truncate font-mono text-[13px] font-semibold text-ink hover:text-accent"
        title={`View ${node.name} in Nodes`}
      >
        {node.name}
      </Link>
      <div className="mt-0.5">
        <TierBadge tier={node.tier} />
      </div>
    </div>
  );
}

export function CertTable({ rows, viewerTimezone, onView, onRenew, onRevoke, onDelete, onDownload }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="fa-shield"
        title="No certificates yet"
        description="Issue your first node certificate to start enrolling middle-tier nodes."
      />
    );
  }
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
              <th className="px-5 py-3">Subject</th>
              <th className="px-5 py-3">Node</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Issued</th>
              <th className="px-5 py-3">Expires</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const canDelete = c.status !== 'active' || c.isExpired;
              const canRenew = c.node && !!c.node.id;
              return (
                <tr key={c.id} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                  <td className="px-5 py-3 align-middle">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
                        <i className="fa-solid fa-shield" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-ink">{c.subjectCn}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-mist">
                          {c.sanDns?.map((d) => (
                            <span key={d} className="inline-flex items-center gap-1 rounded border border-line bg-canvas px-1.5 py-0.5 font-mono">
                              <i className="fa-solid fa-globe text-[8px]" aria-hidden="true" />
                              {d}
                            </span>
                          ))}
                          {c.sanUri?.map((u) => (
                            <span key={u} className="inline-flex items-center gap-1 rounded border border-line bg-canvas px-1.5 py-0.5 font-mono">
                              <i className="fa-solid fa-link text-[8px]" aria-hidden="true" />
                              {u}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <NodeCell node={c.node} />
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <StatusBadge cert={c} />
                    {c.status === 'revoked' && c.revocationReason && (
                      <div className="mt-1 text-[10px] uppercase tracking-wider text-mist">
                        {c.revocationReason}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 align-middle text-xs text-mist">
                    {formatUtcInTimezone(c.notBefore, viewerTimezone)}
                  </td>
                  <td className="px-5 py-3 align-middle text-xs">
                    <span className={c.isExpired ? 'text-mist' : 'text-ink'}>
                      {formatUtcInTimezone(c.notAfter, viewerTimezone)}
                    </span>
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onView(c)}
                        title="View details"
                        aria-label={`View ${c.subjectCn}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                      >
                        <i className="fa-solid fa-eye" aria-hidden="true" />
                      </button>
                      <a
                        href={onDownload(c)}
                        download={`${sanitize(c.subjectCn)}.crt`}
                        title="Download certificate"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                      >
                        <i className="fa-solid fa-download" aria-hidden="true" />
                      </a>
                      <button
                        type="button"
                        onClick={() => onRenew(c)}
                        disabled={!canRenew}
                        title={canRenew ? 'Renew (issue replacement, revoke this one)' : 'Cannot renew an orphan certificate'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-accent-soft hover:text-accent disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-mist"
                      >
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRevoke(c)}
                        disabled={c.status !== 'active'}
                        title={c.status === 'active' ? 'Revoke' : 'Already revoked'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-warning-soft hover:text-warning disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-mist"
                      >
                        <i className="fa-solid fa-ban" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(c)}
                        disabled={!canDelete}
                        title={canDelete ? 'Delete from history' : 'Revoke or wait until expiry first'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-mist"
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function sanitize(s) {
  return String(s || 'cert').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 64);
}
